import {
  CENTER_INDEX,
  COLOR_ORDER,
  FACE_CONFIG,
  FACE_ORDER,
  assertFace,
  assertStickerIndex,
  isLockedSticker,
} from './cube-config.js';

const SERIALIZATION_VERSION = 1;

function cloneStickers(stickers) {
  return Object.fromEntries(FACE_ORDER.map((face) => [face, [...stickers[face]]]));
}

function createEntryStickers(order) {
  const stickerCount = order * order;
  return Object.fromEntries(FACE_ORDER.map((face) => {
    const values = Array(stickerCount).fill(null);
    if (order === 3) values[CENTER_INDEX] = FACE_CONFIG[face].color;
    return [face, values];
  }));
}

function validateStickerData(order, stickers) {
  const stickerCount = order * order;
  if (!stickers || typeof stickers !== 'object') throw new TypeError('贴纸数据缺失');

  for (const face of FACE_ORDER) {
    if (!Array.isArray(stickers[face]) || stickers[face].length !== stickerCount) {
      throw new TypeError(`${face} 面必须包含 ${stickerCount} 个贴纸`);
    }
    for (const color of stickers[face]) {
      if (color !== null && !COLOR_ORDER.includes(color)) {
        throw new TypeError(`${face} 面包含未知颜色：${color}`);
      }
    }
  }
}

export class CubeState {
  #history = [];

  constructor({ order = 3, stickers = null } = {}) {
    if (![2, 3].includes(order)) throw new RangeError('录入状态仅支持 2×2 或 3×3 魔方');
    this.order = order;
    this.stickers = stickers ? cloneStickers(stickers) : createEntryStickers(order);
    validateStickerData(this.order, this.stickers);
    this.#enforceLockedCenters();
  }

  static solved(order = 3) {
    const stickers = Object.fromEntries(FACE_ORDER.map((face) => (
      [face, Array(order * order).fill(FACE_CONFIG[face].color)]
    )));
    return new CubeState({ order, stickers });
  }

  static deserialize(serialized) {
    const data = typeof serialized === 'string' ? JSON.parse(serialized) : serialized;
    if (!data || data.version !== SERIALIZATION_VERSION) {
      throw new TypeError('录入数据版本不受支持');
    }
    if (data.faceOrder?.join(',') !== FACE_ORDER.join(',')) {
      throw new TypeError('录入数据的面顺序不匹配');
    }
    validateStickerData(data.order, data.stickers);
    return new CubeState({ order: data.order, stickers: data.stickers });
  }

  #enforceLockedCenters() {
    if (this.order !== 3) return;
    for (const face of FACE_ORDER) this.stickers[face][CENTER_INDEX] = FACE_CONFIG[face].color;
  }

  isLocked(face, index) {
    assertFace(face);
    assertStickerIndex(index, this.order);
    return isLockedSticker(this.order, face, index);
  }

  get(face, index) {
    assertFace(face);
    assertStickerIndex(index, this.order);
    return this.stickers[face][index];
  }

  paint(face, index, color) {
    assertFace(face);
    assertStickerIndex(index, this.order);
    if (!COLOR_ORDER.includes(color)) throw new TypeError(`未知颜色：${color}`);
    if (this.isLocked(face, index)) return { changed: false, reason: 'locked' };

    const previous = this.stickers[face][index];
    if (previous === color) return { changed: false, reason: 'unchanged' };
    const counts = this.countColors();
    if (counts[color] >= this.order * this.order) return { changed: false, reason: 'color-full' };

    this.stickers[face][index] = color;
    this.#history.push({ type: 'paint', face, index, previous, next: color });
    return { changed: true };
  }

  undo() {
    const operation = this.#history.pop();
    if (!operation) return false;

    if (operation.type === 'paint') {
      this.stickers[operation.face][operation.index] = operation.previous;
    } else if (operation.type === 'reset') {
      this.stickers = cloneStickers(operation.previous);
    }
    this.#enforceLockedCenters();
    return true;
  }

  reset() {
    const previous = cloneStickers(this.stickers);
    const empty = createEntryStickers(this.order);
    const changed = FACE_ORDER.some((face) => (
      previous[face].some((color, index) => color !== empty[face][index])
    ));
    if (!changed) return false;
    this.stickers = empty;
    this.#history.push({ type: 'reset', previous });
    return true;
  }

  countColors() {
    const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
    for (const face of FACE_ORDER) {
      for (const color of this.stickers[face]) {
        if (color !== null) counts[color] += 1;
      }
    }
    return counts;
  }

  get filledCount() {
    return Object.values(this.countColors()).reduce((sum, count) => sum + count, 0);
  }

  get canUndo() {
    return this.#history.length > 0;
  }

  serialize() {
    return JSON.stringify({
      version: SERIALIZATION_VERSION,
      order: this.order,
      faceOrder: FACE_ORDER,
      stickers: cloneStickers(this.stickers),
    });
  }
}
